/**
 * Ready-made lists, each a FacetedList with its columns and queries bound.
 *
 * Every one takes an optional scope and URL prefix, so the same list can be
 * the whole page or a section of a dossier. That is what removed the
 * unbounded nested fetches: a municipality's procurements are now the
 * procurement list scoped to that buyer, paged and filtered like any other.
 */

import { FacetedList } from './FacetedList';
import { Amount, Badge, Link } from './ui';
import { date, money, moneyExact, num, truncate, mandateTerm, nominationFor } from '../lib/format';
import { tenderFacetSet, type TenderScope } from '../facets/tenders';
import { companyFacetSet, type CompanyScope } from '../facets/companies';
import {
  municipalityFacetSet, personFacetSet, subsidyFacetSet, type SubsidyScope,
} from '../facets/simple';
import type { Column } from './DataTable';
import {
  COMPANY_FACETS, COMPANY_ROWS, MUNICIPALITY_FACETS, MUNICIPALITY_ROWS, PERSON_FACETS,
  PERSON_ROWS, SUBSIDY_FACETS, SUBSIDY_ROWS, TENDER_FACETS, TENDER_ROWS,
} from '../queries/lists';
import type { ResultOf } from '@graphql-typed-document-node/core';
import type { ExportColumn } from '../lib/xlsx';

type TenderRow = NonNullable<ResultOf<typeof TENDER_ROWS>['tenders']>[number];
type CompanyRow = NonNullable<ResultOf<typeof COMPANY_ROWS>['companies']>[number];
type PersonRow = NonNullable<ResultOf<typeof PERSON_ROWS>['people']>[number];
type MunicipalityRow = NonNullable<ResultOf<typeof MUNICIPALITY_ROWS>['municipalities']>[number];
type SubsidyRow = NonNullable<ResultOf<typeof SUBSIDY_ROWS>['subsidies']>[number];



/* ------------------------------------------------------------ procurement */

const TENDER_COLUMNS: readonly Column<TenderRow>[] = [
  {
    key: 'title',
    label: 'Предмет',
    render: (row) => (
      <div>
        <Link href={`#/tenders/${encodeURIComponent(row.tender_id)}`}>
          {truncate(row.title ?? row.tender_id, 74)}
        </Link>
        <div className="uic mono">
          {[row.tender_id, row.procedure_type, row.cpv_description]
            .filter(Boolean).map((part) => truncate(part as string, 38)).join(' · ')}
        </div>
      </div>
    ),
  },
  {
    key: 'procuring_entity',
    label: 'Възложител',
    sortable: true,
    render: (row) => {
      /* The buyer is a body with a page of its own — a ministry has a
         dossier, an agency or directorate has a company page — so the
         name is a link wherever the tender resolved to one. Routing is
         by the portfolio property rather than by reading the name,
         because a ministry's name in this graph is frequently whatever
         string a procurement notice used: МРРБ is filed as "ДЪРЖАВАТА
         МРРБ СОФИЯ".

         Falls back to the published buyer string. 6,779 tenders resolved
         to no body at all, and for those the string is all there is —
         better shown plainly than dressed as a link that goes nowhere. */
      const buyer = row.procuredBy[0];
      if (!buyer) return truncate(row.procuring_entity ?? '—', 30);
      const href = buyer.portfolio
        ? `#/ministries/${buyer.uic}`
        : `#/companies/${buyer.uic}`;
      return (
        <Link href={href}>
          {truncate(buyer.name ?? row.procuring_entity ?? buyer.uic, 30)}
        </Link>
      );
    },
  },
  {
    key: 'winner',
    label: 'Изпълнител',
    render: (row) => {
      const winner = row.winners[0];
      return winner
        ? <Link href={`#/companies/${winner.uic}`}>{truncate(winner.name ?? winner.uic, 26)}</Link>
        : '—';
    },
  },
  {
    key: 'flags',
    label: '',
    render: (row) => (
      <span style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>
        {row.offers_count === 1 && <Badge kind="debt">1 оферта</Badge>}
        {row.is_eu_funded && <Badge kind="municipality">ЕС</Badge>}
        {(row.winner_count ?? 0) > 1 && (
          <Badge kind="consortium">{row.winner_count} изп.</Badge>
        )}
      </span>
    ),
  },
  {
    key: 'publication_date',
    label: 'Дата',
    sortable: true,
    num: true,
    render: (row) => date(row.publication_date),
  },
  {
    key: 'value_bgn',
    label: 'Стойност',
    sortable: true,
    num: true,
    render: (row) => (
      <Amount value={row.awarded_amount ?? row.estimated_value} currency={row.currency} />
    ),
  },
];

/* Отделните CPV колони и двете суми не са дублиране: файлът е за смятане,
   и една обединена колона би върнала четящия към ръчното разделяне. */
const TENDER_EXPORT: readonly ExportColumn<TenderRow>[] = [
  { header: 'Номер', value: (row) => row.tender_id },
  { header: 'Предмет', value: (row) => row.title },
  { header: 'Възложител', value: (row) => row.procuring_entity },
  { header: 'Община', value: (row) => row.commissionedBy[0]?.name },
  { header: 'Изпълнители', value: (row) => row.winners.map((w) => w.name ?? w.uic).join('; ') },
  { header: 'ЕИК на изпълнителите', value: (row) => row.winners.map((w) => w.uic).join('; ') },
  { header: 'Дата', value: (row) => row.publication_date },
  { header: 'Процедура', value: (row) => row.procedure_type },
  { header: 'CPV код', value: (row) => row.cpv_code },
  { header: 'CPV описание', value: (row) => row.cpv_description },
  { header: 'Оферти', value: (row) => row.offers_count },
  {
    header: 'Финансиране от ЕС',
    // the notice can leave it unstated, which is not "не"
    value: (row) => (row.is_eu_funded == null ? '' : (row.is_eu_funded ? 'да' : 'не')),
  },
  { header: 'Възложена сума', format: 'money', value: (row) => row.awarded_amount },
  { header: 'Прогнозна сума', format: 'money', value: (row) => row.estimated_value },
  { header: 'Валута', value: (row) => row.currency },
  { header: 'Стойност (лв.)', format: 'money', value: (row) => row.value_bgn },
];

/**
 * Drops the columns a scope has already decided.
 *
 * A dossier list is scoped to the entity whose page it is on, so the
 * column naming that entity repeats down every row — and where the cell
 * is a link, it links back to the page being read. It costs a column of
 * width to say nothing. Which column that is depends on the scope, so it
 * is decided per list rather than baked into the column set: a
 * municipality's procurement list keeps its buyer column, because there
 * the buyers genuinely differ.
 *
 * The XLSX export is deliberately untouched. A spreadsheet is read away
 * from the page that made the column redundant.
 */
function withoutFixed<TRow>(
  columns: readonly Column<TRow>[],
  scopedBy: Record<string, readonly string[] | undefined>,
): readonly Column<TRow>[] {
  const fixed = new Set(
    Object.entries(scopedBy).filter(([, value]) => value?.length).map(([key]) => key));
  return fixed.size ? columns.filter((column) => !fixed.has(column.key)) : columns;
}

export function TenderList({ prefix = 't.', scope, heading, headingAside }: {
  prefix?: string; scope?: TenderScope; heading?: string; headingAside?: string;
}) {
  const columns = withoutFixed(TENDER_COLUMNS, {
    procuring_entity: scope?.buyerUics,
    winner: scope?.winnerUics,
  });

  return (
    <FacetedList
      facets={tenderFacetSet(prefix, scope ?? {})}
      listDocument={TENDER_ROWS}
      facetsDocument={TENDER_FACETS}
      selectRows={(data) => ({
        rows: data.tenders, total: data.tendersAggregate.count ?? 0,
      })}
      selectFacets={(data) => data.tenderFacets}
      columns={columns}
      rowKey={(row) => row.tender_id}
      defaultSort={{ key: 'value_bgn', dir: 'DESC' }}
      searchPlaceholder="Търси по предмет, възложител или номер…"
      exportable={{ name: 'поръчки', columns: TENDER_EXPORT }}
      {...(heading ? { heading } : {})}
      {...(headingAside ? { headingAside } : {})}
    />
  );
}

/* ---------------------------------------------------------------- companies */

const COMPANY_COLUMNS: readonly Column<CompanyRow>[] = [
  {
    key: 'name',
    label: 'Фирма',
    sortable: true,
    render: (row) => (
      <div>
        <Link href={`#/companies/${row.uic}`}>{row.name ?? row.uic}</Link>
        <div className="uic mono">{row.uic}</div>
      </div>
    ),
  },
  { key: 'legal_form', label: 'Форма', sortable: true, render: (row) => row.legal_form ?? '—' },
  {
    key: 'seat',
    label: 'Седалище',
    render: (row) => {
      const seat = row.seatedIn[0];
      return seat
        ? <Link href={`#/municipalities/${encodeURIComponent(seat.key)}`}>{seat.name}</Link>
        : '—';
    },
  },
  {
    key: 'treasury',
    label: 'От бюджета',
    num: true,
    render: (row) => {
      const paid = row.treasuryPayments[0];
      return paid
        ? (
          <span
            className="amount"
            title={`${moneyExact(paid.amount ?? 0)} · ${num(paid.payment_count ?? 0)} плащания (СЕБРА)`}
          >
            {money(paid.amount ?? 0)}
          </span>
        )
        : '—';
    },
  },
  {
    key: 'flags',
    label: 'Данни',
    render: (row) => {
      const marks = [
        row.taxDebtsAggregate?.count
          ? <Badge key="d" kind="debt">{row.taxDebtsAggregate.count} задълж.</Badge> : null,
        row.tendersWonAggregate?.count
          ? <Badge key="t" kind="company">{row.tendersWonAggregate.count} поръчки</Badge> : null,
        row.subsidiesAggregate?.count
          ? <Badge key="s" kind="municipality">{row.subsidiesAggregate.count} субсидии</Badge> : null,
      ].filter(Boolean);
      return marks.length
        ? <span style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>{marks}</span>
        : '—';
    },
  },
];

const COMPANY_EXPORT: readonly ExportColumn<CompanyRow>[] = [
  { header: 'ЕИК', value: (row) => row.uic },
  { header: 'Фирма', value: (row) => row.name },
  { header: 'Правна форма', value: (row) => row.legal_form },
  { header: 'Седалище', value: (row) => row.seatedIn[0]?.name },
  { header: 'Поръчки', value: (row) => row.tendersWonAggregate?.count },
  { header: 'Субсидии', value: (row) => row.subsidiesAggregate?.count },
  { header: 'Данъчни задължения', value: (row) => row.taxDebtsAggregate?.count },
  { header: 'От бюджета (лв.)', format: 'money', value: (row) => row.treasuryPayments[0]?.amount },
  { header: 'Плащания от бюджета', value: (row) => row.treasuryPayments[0]?.payment_count },
];

export function CompanyList({ prefix = 'c.', scope, heading, headingAside }: {
  prefix?: string; scope?: CompanyScope; heading?: string; headingAside?: string;
}) {
  return (
    <FacetedList
      facets={companyFacetSet(prefix, scope ?? {})}
      listDocument={COMPANY_ROWS}
      facetsDocument={COMPANY_FACETS}
      selectRows={(data) => ({
        rows: data.companies, total: data.companiesAggregate.count ?? 0,
      })}
      selectFacets={(data) => data.companyFacets}
      columns={withoutFixed(COMPANY_COLUMNS, { seat: scope?.seatKeys })}
      rowKey={(row) => row.uic}
      defaultSort={{ key: 'name', dir: 'ASC' }}
      searchPlaceholder="Търси по име или ЕИК…"
      exportable={{ name: 'фирми', columns: COMPANY_EXPORT }}
      {...(heading ? { heading } : {})}
      {...(headingAside ? { headingAside } : {})}
    />
  );
}

/* ------------------------------------------------------------------- people */

const PERSON_COLUMNS: readonly Column<PersonRow>[] = [
  {
    key: 'name',
    label: 'Лице',
    sortable: true,
    render: (row) => (
      <Link href={`#/people/${row.person_hash}`}>{row.name ?? 'Без име'}</Link>
    ),
  },
  {
    key: 'roles',
    label: 'Роли',
    render: (row) => {
      /* Most recent office of each kind. A person can be a councillor in
         one term and a mayor in the next, and the badge that matters is
         the one that says which they are now. */
      const seat = row.positionsConnection.edges.find(
        (edge) => edge.properties.role === 'общински съветник');
      const village = row.positionsConnection.edges.find(
        (edge) => edge.properties.role !== 'общински съветник');
      const roles = [
        row.mayorOf[0] ? <Badge key="m" kind="municipality">кмет · {row.mayorOf[0].name}</Badge> : null,
        seat
          ? (
            <Badge key="c" kind="party">
              съветник · {truncate(seat.node.name ?? '', 22)}
            </Badge>
          )
          : null,
        village
          ? (
            <Badge key="v" kind="municipality">
              кмет на кметство · {truncate(village.properties.area ?? village.node.name ?? '', 20)}
            </Badge>
          )
          : null,
        row.ownedCompaniesAggregate?.count
          ? <Badge key="o" kind="company">собственик ×{row.ownedCompaniesAggregate.count}</Badge> : null,
        row.managedCompaniesAggregate?.count
          ? <Badge key="g" kind="person">управител ×{row.managedCompaniesAggregate.count}</Badge> : null,
      ].filter(Boolean);
      return roles.length
        ? <span style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>{roles}</span>
        : '—';
    },
  },
  {
    key: 'company_links',
    label: 'Фирми',
    sortable: true,
    num: true,
    render: (row) => num(row.company_links),
  },
  {
    key: 'party',
    label: 'Партия',
    render: (row) => {
      const party = row.nominatedBy[0];
      return party
        ? <Link href={`#/parties/${encodeURIComponent(party.key)}`}>{truncate(party.name, 34)}</Link>
        : '—';
    },
  },
];

const PERSON_EXPORT: readonly ExportColumn<PersonRow>[] = [
  { header: 'Лице', value: (row) => row.name },
  // the pseudonymised identifier, whole: it is what makes two exports joinable
  { header: 'Идентификатор', value: (row) => row.person_hash },
  { header: 'Кмет на', value: (row) => row.mayorOf[0]?.name },
  { header: 'Партия', value: (row) => row.nominatedBy[0]?.name },
  { header: 'Свързани фирми', value: (row) => row.company_links },
  { header: 'Собственик във', value: (row) => row.ownedCompaniesAggregate?.count },
  { header: 'Управител във', value: (row) => row.managedCompaniesAggregate?.count },
];

export function PersonList({ prefix = 'p.' }: { prefix?: string }) {
  return (
    <FacetedList
      facets={personFacetSet(prefix)}
      listDocument={PERSON_ROWS}
      facetsDocument={PERSON_FACETS}
      selectRows={(data) => ({
        rows: data.people, total: data.peopleAggregate.count ?? 0,
      })}
      selectFacets={(data) => data.personFacets}
      columns={PERSON_COLUMNS}
      rowKey={(row) => row.person_hash}
      defaultSort={{ key: 'name', dir: 'ASC' }}
      searchPlaceholder="Търси по име…"
      exportable={{ name: 'лица', columns: PERSON_EXPORT }}
    />
  );
}

/* ----------------------------------------------------------- municipalities */

/* A municipality carries one mandate per election since 2015, so the first
   edge is an arbitrary one of three. The sitting mandate is the one no
   later election has closed. */
function sittingMandate(row: MunicipalityRow) {
  const edges = row.mayorsConnection.edges;
  return edges.find((edge) => edge.properties.term_end == null)
    ?? [...edges].sort((a, b) =>
      (b.properties.elected_year ?? 0) - (a.properties.elected_year ?? 0))[0]
    ?? null;
}

const MUNICIPALITY_COLUMNS: readonly Column<MunicipalityRow>[] = [
  {
    key: 'name',
    label: 'Община',
    sortable: true,
    render: (row) => (
      <Link href={`#/municipalities/${encodeURIComponent(row.key)}`}>{row.name ?? row.key}</Link>
    ),
  },
  { key: 'province', label: 'Област', sortable: true, render: (row) => row.province ?? '—' },
  {
    key: 'mayor',
    label: 'Кмет',
    render: (row) => {
      const mandate = sittingMandate(row);
      if (!mandate) return '—';
      return (
        <>
          <Link href={`#/people/${mandate.node.person_hash}`}>{mandate.node.name}</Link>
          <div className="uic mono">{mandateTerm(mandate.properties)}</div>
        </>
      );
    },
  },
  {
    key: 'party',
    label: 'Партия',
    render: (row) => {
      const party = nominationFor(sittingMandate(row));
      return party
        ? <Link href={`#/parties/${encodeURIComponent(party.key)}`}>{truncate(party.name, 28)}</Link>
        : '—';
    },
  },
  {
    key: 'population_permanent',
    label: 'Население',
    sortable: true,
    num: true,
    render: (row) => num(row.population_permanent),
  },
  {
    key: 'tender_total_bgn',
    label: 'Възложено',
    sortable: true,
    num: true,
    render: (row) => (row.tender_total_bgn == null ? '—' : (
      <div>
        <Amount value={row.tender_total_bgn} />
        <div className="uic mono">{`${num(row.tendersAggregate?.count)} поръчки`}</div>
      </div>
    )),
  },
  {
    /* The risk lens this table exists for: the total divided by who lives
       there. Sorted descending it surfaces the municipality of four
       thousand people spending like a city — the absolute column above
       only ever ranks by size. Blank where ГРАО publishes no population;
       the division is never done against a guess. */
    key: 'spend_per_capita',
    label: 'На жител',
    sortable: true,
    num: true,
    render: (row) => <Amount value={row.spend_per_capita} />,
  },
];

const MUNICIPALITY_EXPORT: readonly ExportColumn<MunicipalityRow>[] = [
  { header: 'Община', value: (row) => row.name ?? row.key },
  { header: 'Област', value: (row) => row.province },
  { header: 'Кмет', value: (row) => sittingMandate(row)?.node.name },
  { header: 'Партия', value: (row) => nominationFor(sittingMandate(row))?.name },
  { header: 'Население (постоянен адрес)', value: (row) => row.population_permanent },
  { header: 'Поръчки', value: (row) => row.tendersAggregate?.count },
  { header: 'Възложено (лв.)', format: 'money', value: (row) => row.tender_total_bgn },
  { header: 'На жител (лв.)', format: 'money', value: (row) => row.spend_per_capita },
];

export function MunicipalityList({ prefix = 'm.' }: { prefix?: string }) {
  return (
    <FacetedList
      facets={municipalityFacetSet(prefix)}
      listDocument={MUNICIPALITY_ROWS}
      facetsDocument={MUNICIPALITY_FACETS}
      selectRows={(data) => ({
        rows: data.municipalities, total: data.municipalitiesAggregate.count ?? 0,
      })}
      selectFacets={(data) => data.municipalityFacets}
      columns={MUNICIPALITY_COLUMNS}
      rowKey={(row) => row.key}
      /* Opens on the risk reading, not on size: sorted by population the
         table is province geography; sorted by spend per head, row one is
         the municipality whose procurement is out of scale with its
         people — the thing this register exists to surface. */
      defaultSort={{ key: 'spend_per_capita', dir: 'DESC' }}
      searchPlaceholder="Търси община…"
      exportable={{ name: 'общини', columns: MUNICIPALITY_EXPORT }}
    />
  );
}

/* ---------------------------------------------------------------- subsidies */

const SUBSIDY_COLUMNS: readonly Column<SubsidyRow>[] = [
  {
    key: 'measure',
    label: 'Мярка / проект',
    sortable: true,
    render: (row) => (
      <div>
        <Link href={`#/subsidies/${encodeURIComponent(row.subsidy_id)}`}>
          {truncate(row.measure ?? row.subsidy_id, 60)}
        </Link>
        {row.project_title && <div className="uic">{truncate(row.project_title, 68)}</div>}
      </div>
    ),
  },
  {
    key: 'beneficiary',
    label: 'Бенефициент',
    render: (row) => {
      const who = row.beneficiaries[0];
      return who
        ? <Link href={`#/companies/${who.uic}`}>{truncate(who.name ?? who.uic, 30)}</Link>
        : '—';
    },
  },
  { key: 'source', label: 'Източник', sortable: true, render: (row) => row.source ?? '—' },
  { key: 'year', label: 'Година', sortable: true, num: true, render: (row) => row.year ?? '—' },
  {
    key: 'amount',
    label: 'Договорено',
    sortable: true,
    num: true,
    render: (row) => <Amount value={row.amount} />,
  },
  {
    /* Contracted and disbursed are different quantities, and for a project
       still running they are very different: the София–Пловдив rail
       modernisation is contracted at 806m лв and paid 108m. Shown beside
       the grant rather than instead of it — the contract is the commitment
       and both are facts — with a dash where ИСУН publishes no tranches,
       which is most of ДФЗ and a third of ИСУН. */
    key: 'paid_amount',
    label: 'Изплатено',
    sortable: true,
    num: true,
    render: (row) => (row.paid_amount == null
      ? <span className="muted">—</span>
      : <Amount value={row.paid_amount} />),
  },
];

const SUBSIDY_EXPORT: readonly ExportColumn<SubsidyRow>[] = [
  { header: 'Номер', value: (row) => row.subsidy_id },
  { header: 'Мярка', value: (row) => row.measure },
  { header: 'Проект', value: (row) => row.project_title },
  { header: 'Бенефициент', value: (row) => row.beneficiaries[0]?.name },
  { header: 'ЕИК', value: (row) => row.beneficiaries[0]?.uic },
  { header: 'Източник', value: (row) => row.source },
  { header: 'Година', value: (row) => row.year },
  { header: 'Договорено (лв.)', format: 'money', value: (row) => row.amount },
  { header: 'Изплатено (лв.)', format: 'money', value: (row) => row.paid_amount },
  { header: 'Състояние', value: (row) => row.status },
  { header: 'Сума (лв.)', format: 'money', value: (row) => row.amount },
];

export function SubsidyList({ prefix = 's.', scope, heading }: {
  prefix?: string; scope?: SubsidyScope; heading?: string;
}) {
  return (
    <FacetedList
      facets={subsidyFacetSet(prefix, scope ?? {})}
      listDocument={SUBSIDY_ROWS}
      facetsDocument={SUBSIDY_FACETS}
      selectRows={(data) => ({
        rows: data.subsidies, total: data.subsidiesAggregate.count ?? 0,
      })}
      selectFacets={(data) => data.subsidyFacets}
      columns={withoutFixed(SUBSIDY_COLUMNS, { beneficiary: scope?.beneficiaryUics })}
      rowKey={(row) => row.subsidy_id}
      defaultSort={{ key: 'amount', dir: 'DESC' }}
      searchPlaceholder="Търси по мярка или проект…"
      exportable={{ name: 'субсидии', columns: SUBSIDY_EXPORT }}
      {...(heading ? { heading } : {})}
    />
  );
}
